import { useEffect, useRef } from 'react';

/**
 * Space runs the one action that matters right now - start, pause, resume
 * the Focus timer; start and stop a breathing session. The shortcut every
 * timer app teaches. Ignored while typing (an input, a
 * textarea, a contenteditable) and while a modifier is held, and never when
 * focus is on a button, link or the dial, where Space already means "press
 * this" (or nothing, rather than a surprise start).
 */
export function isInteractive(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (
    target.closest(
      'input, textarea, select, button, a, [role="button"], [role="radio"], [role="slider"]'
    )
  ) {
    return true;
  }
  // A dialog keeps its own keys - except focus mode, which IS the timer and
  // marks itself with `data-timer-shortcuts`.
  if (target.closest('[data-timer-shortcuts]')) return false;
  return Boolean(target.closest('[role="dialog"]'));
}

export function useSpaceShortcut<Phase extends string>(
  phase: Phase,
  actions: Record<Phase, () => void>
): void {
  // Latest actions without re-binding the listener on every render.
  const latest = useRef(actions);
  latest.current = actions;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isInteractive(event.target)) return;
      event.preventDefault();
      latest.current[phase]();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phase]);
}
